/**
 * Composio Action Global Tool
 *
 * Bridges connected Composio tools into the orchestrator.
 * When a user has connected apps (Gmail, Calendar, etc.), this tool
 * enables the AI to execute actions on their behalf.
 *
 * The orchestrator routes requests like "send an email" or "check my calendar"
 * to this tool, which identifies the right Composio action and executes it.
 */

import { getConnectedAccounts, listTools, executeToolAction } from '../../composio/ComposioClient';
import { offerConnectForQuery } from './connectApp';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import { detectInputLanguage } from '../../agenticWorkflows/languageMirror';
import { generateSpecialMessageTemplate } from '../../agenticWorkflows/helpers';
import { makeRegexTrigger } from '../triggerHelpers';
import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
} from '../types';

// ============================================================================
// Constants
// ============================================================================

const COMPOSIO_THEME: ToolTheme = {
  accent: '#6366F1',   // Indigo 500
  accentBg: '#EEF2FF', // Indigo 50
  border: '#818CF8',   // Indigo 400
  subtitle: '#475569', // Slate 600
  muted: '#64748b',    // Slate 500
};

const BUTTON_IDS = {
  composioAction: 'global-tool-composio-action-btn',
};

/**
 * Trigger patterns for Composio-powered actions.
 * These match common requests that map to connected tool capabilities.
 *
 * Phase 0 hardening: all patterns are anchored to the start of the message
 * (`^…`) so they only fire on direct command-style triggers, not on
 * mentions ("…and don't send an email about it"). Routing through
 * `makeRegexTrigger` adds the negation guard for completeness.
 */
const COMPOSIO_TRIGGER_PATTERNS: RegExp[] = [
  // Email
  /^(send|compose|draft|write)\s+(an?\s+)?email/i,
  /^(check|read|show|get)\s+(my\s+)?(emails?|inbox|mail)/i,
  // Calendar
  /^(create|add|schedule|set\s+up)\s+(an?\s+)?(event|meeting|appointment|calendar)/i,
  /^(check|show|get|what'?s?\s+on)\s+(my\s+)?calendar/i,
  /^(what|show)\s+(are\s+)?my\s+(upcoming\s+)?(events|meetings|appointments)/i,
  // General integration
  /^(use|run|execute)\s+composio/i,
];

const matchComposioTrigger = makeRegexTrigger(COMPOSIO_TRIGGER_PATTERNS);

// ============================================================================
// State
// ============================================================================

export interface ComposioActionState {
  isExecuting: boolean;
  connectedApps: string[];
  selectedAction?: string;
  result?: any;
}

// ============================================================================
// Tool Definition
// ============================================================================

export const composioActionTool: GlobalToolDefinition<ComposioActionState> = {
  id: 'composio-action',
  name: 'Connected App Action',
  description: 'Execute actions using connected apps (Gmail, Calendar, Slack, etc.)',

  triggerPattern: matchComposioTrigger,

  createInitialState: (): ComposioActionState => ({
    isExecuting: false,
    connectedApps: [],
  }),

  steps: [
    // ========================================================================
    // Step 1: Resolve — check connected apps and find the right action
    // ========================================================================
    {
      id: 'resolve',
      name: 'Find Action',
      activeDisplayName: 'checking connected apps',

      execute: async (
        ctx: StepContext<ComposioActionState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as { query?: string; toolkitSlug?: string; actionSlug?: string } | undefined;
        const userQuery = input?.query || ctx.triggerMessage.text;

        try {
          // Get user's connected accounts
          const accounts = await getConnectedAccounts();
          const activeAccounts = accounts.filter(
            (a) => a.status?.toUpperCase() === 'ACTIVE'
          );

          if (activeAccounts.length === 0) {
            // V1 in-chat connect (PLAN-storage-folder-permissions §7.3,
            // 2026-08-11): if the query implies a specific catalog app,
            // delegate to the connect-app offer card instead of dead-ending.
            try {
              // lang: the connect card's {en, es} chrome follows the ask
              // (2026-09-02 review — this lane rendered English for Spanish).
              const offered = await offerConnectForQuery(userQuery, ctx.messaging, {
                lang: detectInputLanguage(userQuery),
              });
              if (offered) {
                return { status: 'completed', nextStep: null };
              }
            } catch {
              // Non-fatal — fall through to the static pointer below.
            }
            ctx.messaging.showSpecialMessage({
              header: { title: 'No Connected Apps', icon: '🔌' },
              sections: [
                {
                  type: 'text',
                  content: 'You don\'t have any apps connected yet. Go to the <a href="/integrations" style="color:#6366F1;text-decoration:underline;">Integrations page</a> to connect Gmail, Google Calendar, Slack, and more — or just tell me to connect an app (for example, "connect Gmail") and I\'ll set it up right here in the chat.',
                },
              ],
              theme: 'blue',
            });
            return { status: 'completed', nextStep: null };
          }

          const connectedSlugs = activeAccounts.map((a) => a.toolkit.slug);
          ctx.setState({ connectedApps: connectedSlugs });

          // Master rule (Primacy Audit D3, 2026-08-24): the toolkit comes
          // from the DECIDER's input, never from a keyword scan of the user's
          // words. Until 2026-08-24 a hardcoded phrase list picked which
          // real-world surface this ran against (the same pattern class the
          // owner killed once before — the Gmail-offer-to-an-Outlook-firm
          // table); absent a named toolkit we now say so honestly instead.
          const toolkitSlug = input?.toolkitSlug || null;

          if (!toolkitSlug) {
            // Show available connected apps (honest refusal copy — D9 class).
            const appList = connectedSlugs.map((s) => `• ${s}`).join('\n');
            ctx.messaging.showSpecialMessage({
              header: { title: 'Connected Apps', icon: '🔌' },
              sections: [
                {
                  type: 'text',
                  content: `I couldn't tell which app this should run on, so I didn't guess. Here are your connected apps:\n\n${appList}\n\nTell me which app to use and I'll take it from there.`,
                },
              ],
              theme: 'blue',
            });
            return { status: 'completed', nextStep: null };
          }

          // Get available tools for this toolkit
          const tools = await listTools([toolkitSlug]);

          if (!tools || tools.length === 0) {
            ctx.messaging.showSpecialMessage({
              header: { title: 'No Actions Available', icon: '⚠️' },
              sections: [
                {
                  type: 'text',
                  content: `${toolkitSlug} is connected but no actions are available. This may be a temporary issue.`,
                },
              ],
              theme: 'amber',
            });
            return { status: 'completed', nextStep: null };
          }

          // The action, too, is the decider's to name (Primacy Audit D3):
          // no intent-phrase map, no keyword-overlap scoring. A null action
          // carries forward to the execute step's "which action?" listing.
          const actionSlug = input?.actionSlug || null;

          return {
            status: 'completed',
            carryForward: {
              toolkitSlug,
              actionSlug,
              userQuery,
              availableTools: tools.slice(0, 10).map((t: any) => ({
                slug: t.slug,
                description: t.description,
              })),
            },
          };
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          ctx.messaging.showSpecialMessage({
            header: { title: 'Connection Error', icon: '❌' },
            sections: [
              {
                type: 'text',
                content: `Failed to check connected apps: ${errorMessage}`,
              },
            ],
            theme: 'red',
          });
          return { status: 'failed', error: new Error(errorMessage) };
        }
      },
    },

    // ========================================================================
    // Step 2: Execute — run the Composio action
    // ========================================================================
    {
      id: 'execute',
      name: 'Execute Action',
      activeDisplayName: 'running action',

      execute: async (
        ctx: StepContext<ComposioActionState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as {
          toolkitSlug: string;
          actionSlug: string;
          userQuery: string;
          availableTools: Array<{ slug: string; description: string }>;
        } | undefined;

        if (!input?.actionSlug) {
          // If we couldn't determine the action, show available actions
          const toolsList = (input?.availableTools || [])
            .map((t) => `• <strong>${t.slug}</strong>: ${t.description}`)
            .join('<br>');

          ctx.messaging.showSpecialMessage({
            header: { title: `${input?.toolkitSlug || 'App'} Actions`, icon: '🔧' },
            sections: [
              {
                type: 'text',
                content: `I found these available actions:<br><br>${toolsList}<br><br>What would you like to do?`,
              },
            ],
            theme: 'blue',
          });
          return { status: 'completed', nextStep: null };
        }

        ctx.setState({ isExecuting: true, selectedAction: input.actionSlug });

        const stream = ctx.messaging.showPending(`running ${input.toolkitSlug} action`);

        try {
          // Build input params from the user's query
          const actionParams = buildActionParams(input.userQuery, input.actionSlug);

          const result = await executeToolAction(input.actionSlug, actionParams);

          ctx.setState({ isExecuting: false, result });

          stream.finalize({
            payload: {
              kind: 'composio.action_complete',
              toolId: 'composio-action',
              data: {
                toolkitSlug: input.toolkitSlug,
                actionSlug: input.actionSlug,
                userQuery: input.userQuery,
                result,
              },
              summary: `composio: ${input.toolkitSlug}/${input.actionSlug} executed successfully`,
            },
            sourceTag: `composio_${input.toolkitSlug}_${input.actionSlug}`,
          });

          return {
            status: 'completed',
            output: { result, toolkitSlug: input.toolkitSlug, actionSlug: input.actionSlug },
          };
        } catch (error) {
          ctx.setState({ isExecuting: false });
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';

          stream.finalize({
            payload: {
              kind: 'composio.action_error',
              toolId: 'composio-action',
              severity: 'error',
              data: {
                toolkitSlug: input.toolkitSlug,
                actionSlug: input.actionSlug,
                errorMessage,
              },
              summary: `composio: ${input.toolkitSlug}/${input.actionSlug} failed`,
            },
            sourceTag: `composio_${input.toolkitSlug}_error`,
          });

          return {
            status: 'failed',
            error: new Error(errorMessage),
            userFacingErrorShown: true,
          };
        }
      },

      cleanup: async (ctx: StepContext<ComposioActionState>) => {
        ctx.setState({ isExecuting: false });
      },
    },
  ],

  isAuthorized: isAgenticWorkflowsAuthorized,

  theme: COMPOSIO_THEME,

  buttonIds: BUTTON_IDS,

  canTriggerFromButton: false,
};

// ============================================================================
// Helper Functions
// ============================================================================

// (inferToolkit / inferAction — the keyword phrase lists and description-
// overlap scorer that used to pick the toolkit and action when the input
// didn't name them — were removed 2026-08-24 under the master rule (Primacy
// Audit D3). The decider names both slugs; this tool only executes. Do NOT
// reintroduce query-text inference here.)

/**
 * Build action parameters from the user's query.
 * For now, returns the raw query — the Composio API + LLM layer handles parameter extraction.
 */
function buildActionParams(query: string, _actionSlug: string): Record<string, any> {
  return { query };
}

/**
 * Format a Composio action result for display in the chat.
 * Specialized formatters for Gmail, Calendar, etc. produce clean summaries.
 */
function formatActionResult(
  result: any,
  toolkitSlug: string,
  actionSlug: string
): { text: string; html: string } {
  const data = result?.data || result;

  if (!data) {
    return {
      text: `${toolkitSlug} action completed successfully.`,
      html: generateSpecialMessageTemplate({
        header: { title: `${toolkitSlug} Action Complete`, icon: '✅' },
        sections: [{ type: 'text', content: 'Action completed successfully.' }],
        theme: 'green',
      }),
    };
  }

  // Try specialized formatters first
  const specialized = formatSpecialized(data, toolkitSlug, actionSlug);
  if (specialized) return specialized;

  // Generic fallback — summarize cleanly instead of dumping JSON
  let displayText: string;
  if (typeof data === 'string') {
    displayText = data;
  } else if (Array.isArray(data)) {
    displayText = data.map((item, i) => `${i + 1}. ${summarizeItem(item)}`).join('\n');
  } else if (typeof data === 'object') {
    displayText = summarizeObject(data);
  } else {
    displayText = String(data);
  }

  return {
    text: displayText,
    html: generateSpecialMessageTemplate({
      header: { title: `${friendlyToolkitName(toolkitSlug)} — ${friendlyActionName(actionSlug)}`, icon: '✅' },
      sections: [
        { type: 'text', content: displayText.replace(/\n/g, '<br>') },
      ],
      theme: 'green',
    }),
  };
}

// ============================================================================
// Specialized Formatters
// ============================================================================

function formatSpecialized(
  data: any,
  toolkitSlug: string,
  actionSlug: string
): { text: string; html: string } | null {
  const slug = actionSlug.toUpperCase();

  // ── Gmail emails ──
  if (slug.includes('GMAIL_FETCH') || slug.includes('GMAIL_LIST') || slug.includes('GMAIL_GET_EMAIL')) {
    return formatGmailEmails(data);
  }

  // ── Gmail draft/send ──
  if (slug.includes('GMAIL_CREATE') || slug.includes('GMAIL_SEND')) {
    return formatGmailSend(data);
  }

  // ── Calendar events ──
  if (slug.includes('CALENDAR_FIND') || slug.includes('CALENDAR_LIST') || slug.includes('EVENTS_LIST')) {
    return formatCalendarEvents(data);
  }

  // ── Calendar create ──
  if (slug.includes('CALENDAR_CREATE')) {
    return formatCalendarCreate(data);
  }

  // ── GitHub ──
  if (toolkitSlug.toLowerCase() === 'github') {
    return formatGithubResult(data, actionSlug);
  }

  return null;
}

function formatGmailEmails(data: any): { text: string; html: string } {
  // Composio returns various shapes — try to find the email list
  const emails = extractArray(data, ['messages', 'emails', 'threads', 'data']);

  if (!emails || emails.length === 0) {
    const text = '📭 No recent emails found.';
    return {
      text,
      html: generateSpecialMessageTemplate({
        header: { title: 'Gmail', icon: '📧' },
        sections: [{ type: 'text', content: text }],
        theme: 'blue',
      }),
    };
  }

  const formatted = emails.slice(0, 10).map((email: any, i: number) => {
    const from = extractField(email, ['from', 'sender', 'senderEmail']) || 'Unknown sender';
    const subject = extractField(email, ['subject', 'title', 'snippet']) || '(no subject)';
    const date = extractField(email, ['date', 'internalDate', 'receivedAt', 'created_at']);
    const snippet = extractField(email, ['snippet', 'preview', 'body_preview', 'textPlain']);

    let line = `**${i + 1}. ${escapeHtml(subject)}**\n   From: ${escapeHtml(from)}`;
    if (date) line += ` · ${formatDate(date)}`;
    if (snippet) {
      // Strip HTML tags and truncate
      const clean = snippet.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, 120);
      if (clean) line += `\n   ${escapeHtml(clean)}${snippet.length > 120 ? '...' : ''}`;
    }
    return line;
  });

  const text = `📧 ${emails.length} recent email${emails.length === 1 ? '' : 's'}:\n\n${formatted.join('\n\n')}`;
  return {
    text,
    html: generateSpecialMessageTemplate({
      header: { title: `Gmail — ${emails.length} Email${emails.length === 1 ? '' : 's'}`, icon: '📧' },
      sections: [{ type: 'text', content: text.replace(/\n/g, '<br>').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') }],
      theme: 'blue',
    }),
  };
}

function formatGmailSend(data: any): { text: string; html: string } {
  // UNIFORMITY (owner directive 2026-08-25, ask 4): a provider id means
  // nothing to the user — this legacy tile drops it like the receipt card
  // did. The id still lives in the structured data for the record.
  const text = '✉️ Email sent.';
  return {
    text,
    html: generateSpecialMessageTemplate({
      header: { title: 'Gmail', icon: '✉️' },
      sections: [{ type: 'text', content: text }],
      // Prest0 gold, not green, for send confirmations (owner directive
      // 2026-08-25, ask 6) — 'amber' is the platform's yellow-family theme.
      theme: 'amber',
    }),
  };
}

function formatCalendarEvents(data: any): { text: string; html: string } {
  const events = extractArray(data, ['event_data', 'events', 'items', 'data']);

  if (!events || events.length === 0) {
    const text = '📅 No events found for today.';
    return {
      text,
      html: generateSpecialMessageTemplate({
        header: { title: 'Google Calendar', icon: '📅' },
        sections: [{ type: 'text', content: text }],
        theme: 'blue',
      }),
    };
  }

  const formatted = events.slice(0, 10).map((event: any, i: number) => {
    const title = extractField(event, ['summary', 'title', 'name', 'subject']) || '(untitled)';
    const start = extractField(event, ['start', 'startTime', 'start_time', 'when']);
    const end = extractField(event, ['end', 'endTime', 'end_time']);
    const location = extractField(event, ['location', 'venue']);

    let line = `**${i + 1}. ${escapeHtml(title)}**`;
    if (start) {
      const startObj = start as any;
      const endObj = end as any;
      const startStr = typeof start === 'object' ? (startObj.dateTime || startObj.date || JSON.stringify(start)) : String(start);
      const endStr = end ? (typeof end === 'object' ? (endObj.dateTime || endObj.date || '') : String(end)) : '';
      line += `\n   🕐 ${formatDate(startStr)}${endStr ? ` — ${formatDate(endStr)}` : ''}`;
    }
    if (location) line += `\n   📍 ${escapeHtml(String(location))}`;
    return line;
  });

  const text = `📅 ${events.length} event${events.length === 1 ? '' : 's'} found:\n\n${formatted.join('\n\n')}`;
  return {
    text,
    html: generateSpecialMessageTemplate({
      header: { title: `Google Calendar — ${events.length} Event${events.length === 1 ? '' : 's'}`, icon: '📅' },
      sections: [{ type: 'text', content: text.replace(/\n/g, '<br>').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') }],
      theme: 'blue',
    }),
  };
}

function formatCalendarCreate(data: any): { text: string; html: string } {
  const title = data?.summary || data?.title || 'Event';
  const text = `📅 "${title}" has been added to your calendar.`;
  return {
    text,
    html: generateSpecialMessageTemplate({
      header: { title: 'Google Calendar', icon: '📅' },
      sections: [{ type: 'text', content: text }],
      theme: 'green',
    }),
  };
}

function formatGithubResult(data: any, actionSlug: string): { text: string; html: string } {
  // Generic GitHub formatting — summarize top-level fields cleanly
  const summary = summarizeObject(data);
  const title = friendlyActionName(actionSlug);
  return {
    text: `🐙 ${title}:\n${summary}`,
    html: generateSpecialMessageTemplate({
      header: { title: `GitHub — ${title}`, icon: '🐙' },
      sections: [{ type: 'text', content: summary.replace(/\n/g, '<br>') }],
      theme: 'blue',
    }),
  };
}

// ============================================================================
// Utility Helpers
// ============================================================================

/** Try to extract an array from nested data by common key names */
function extractArray(data: any, keys: string[]): any[] | null {
  if (Array.isArray(data)) return data;
  if (typeof data !== 'object' || !data) return null;
  for (const key of keys) {
    if (Array.isArray(data[key])) return data[key];
  }
  // Check one level deeper (response_data.messages, etc.)
  for (const val of Object.values(data)) {
    if (typeof val === 'object' && val && !Array.isArray(val)) {
      for (const key of keys) {
        if (Array.isArray((val as any)[key])) return (val as any)[key];
      }
    }
  }
  return null;
}

/** Try to extract a string field by common key names */
function extractField(obj: any, keys: string[]): string | null {
  if (typeof obj !== 'object' || !obj) return null;
  for (const key of keys) {
    const val = obj[key];
    if (val !== null && val !== undefined && val !== '') {
      return typeof val === 'object' ? null : String(val);
    }
  }
  // Check nested payload/headers
  const payload = obj.payload || obj.headers;
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload)) {
      for (const key of keys) {
        const header = payload.find((h: any) => h.name?.toLowerCase() === key.toLowerCase());
        if (header?.value) return String(header.value);
      }
    }
  }
  return null;
}

/** Clean summary of an object (skip huge/binary fields) */
function summarizeObject(data: any): string {
  if (typeof data !== 'object' || !data) return String(data);
  const lines: string[] = [];
  for (const [key, val] of Object.entries(data)) {
    if (val === null || val === undefined || val === '') continue;
    // Skip huge nested objects and tokens
    if (key.toLowerCase().includes('token') || key.toLowerCase().includes('raw')) continue;
    if (typeof val === 'string') {
      const clean = val.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
      if (clean.length > 200) {
        lines.push(`**${key}**: ${clean.slice(0, 200)}...`);
      } else if (clean) {
        lines.push(`**${key}**: ${clean}`);
      }
    } else if (Array.isArray(val)) {
      lines.push(`**${key}**: ${val.length} item${val.length === 1 ? '' : 's'}`);
    } else if (typeof val === 'object') {
      lines.push(`**${key}**: ${summarizeItem(val)}`);
    } else {
      lines.push(`**${key}**: ${String(val)}`);
    }
  }
  return lines.join('\n') || 'Action completed.';
}

function summarizeItem(item: any): string {
  if (typeof item === 'string') return item;
  if (item?.subject) return item.subject;
  if (item?.title) return item.title;
  if (item?.summary) return item.summary;
  if (item?.name) return item.name;
  if (item?.snippet) return item.snippet.slice(0, 80);
  // For small objects, show a brief summary
  const keys = Object.keys(item || {});
  if (keys.length <= 3) {
    return keys.map(k => `${k}: ${String(item[k]).slice(0, 50)}`).join(', ');
  }
  return `(${keys.length} fields)`;
}

function friendlyToolkitName(slug: string): string {
  const map: Record<string, string> = {
    gmail: 'Gmail', googlecalendar: 'Google Calendar', googledrive: 'Google Drive',
    googlesheets: 'Google Sheets', googledocs: 'Google Docs', slack: 'Slack',
    github: 'GitHub', notion: 'Notion', linear: 'Linear', outlook: 'Outlook',
    twitter: 'Twitter', hubspot: 'HubSpot', salesforce: 'Salesforce',
    jira: 'Jira', asana: 'Asana',
  };
  return map[slug.toLowerCase()] || slug;
}

function friendlyActionName(slug: string): string {
  return slug
    .replace(/^[A-Z]+_/, '') // Remove prefix like GMAIL_, GOOGLECALENDAR_
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function formatDate(dateStr: string): string {
  try {
    // Handle Unix timestamps (milliseconds)
    const num = Number(dateStr);
    if (!isNaN(num) && num > 1000000000000) {
      return new Date(num).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return dateStr;
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
